import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'
import { cabinetApi } from '../api'
import type { BBox } from '../api'
import { isobathZoomStep } from './isobaths'
import { presentClasses } from './substrate'

/** Минимум, который хук просит у карты — как у промрайонов. */
export interface SubstrateMapSink {
  getMap: () => maplibregl.Map | null
  getBBox: () => BBox
  setSubstrate: (data: GeoJSON.FeatureCollection) => void
  removeSubstrate: () => void
}

/**
 * Оверлей «Тип дна» (карты ГГК-1000).
 *
 * Даты у слоя нет: грунт не меняется. Есть ВЬЮПОРТ — полигоны режутся и
 * упрощаются по кадру, как у промрайонов. Дебаунс тот же: пан и инерция зума
 * сыплют `moveend`.
 */
const DEBOUNCE_MS = 250

export function useSubstrateLayer(
  mapRef: RefObject<SubstrateMapSink | null>,
  enabled: boolean,
  viewKey: string,
): { classes: number[]; attribution: string | null; disclaimerKey: string | null } {
  // Подпись и легенда — только когда слой что-то нарисовал: вне листов карты
  // грунта нет, и легенда над пустой водой обещала бы то, чего в кадре нет.
  const [meta, setMeta] = useState<{ attribution: string; disclaimerKey: string } | null>(null)
  // Классы, легшие в кадр: легенда называет только их — «смешанного» эти
  // карты не дают, и строка о нём обещала бы то, чего на карте нет.
  const [classes, setClasses] = useState<number[]>([])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeSubstrate()
      setMeta(null)
      setClasses([])
      return
    }
    let alive = true
    const id = setTimeout(() => {
      const zoom = isobathZoomStep(map.getMap()?.getZoom() ?? 6)
      cabinetApi
        .substrate({ bbox: map.getBBox(), zoom })
        .then(fc => {
          if (!alive) return
          if (!fc.features?.length) {
            map.removeSubstrate()
            setMeta(null)
            setClasses([])
            return
          }
          setClasses(presentClasses(fc.features.map(f => f.properties.class5)))
          map.setSubstrate(fc as unknown as GeoJSON.FeatureCollection)
          setMeta(
            fc.meta
              ? { attribution: fc.meta.attribution, disclaimerKey: fc.meta.disclaimer_key }
              : null,
          )
        })
        .catch(() => {
          if (!alive) return
          map.removeSubstrate()
          setMeta(null)
          setClasses([])
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewKey])

  useEffect(
    () => () => mapRef.current?.removeSubstrate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    classes,
    attribution: meta?.attribution ?? null,
    disclaimerKey: meta?.disclaimerKey ?? null,
  }
}
